import { useEffect, useMemo, useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { useGuestStore } from '@/lib/stores/guest-store';
import { trpc } from '@/lib/trpc';
import { normalizeUserProgress, type RawUserProgress } from '@/lib/user-progress';

type GuestData = ReturnType<typeof useGuestStore.getState>['onboardingData'];

/**
 * Creates a guest user progress object with default values.
 * This is a pure function that can be easily tested.
 */
function createGuestUserProgress(guestData: GuestData): RawUserProgress {
  return {
    name: guestData?.name || 'Guest Stargazer',
    sign: guestData?.zodiacSign || 'Aries',
    dob: guestData?.birthDate || null,
    birthTime: guestData?.birthTime || null,
    birthPlace: guestData?.birthPlace || null,
    birthPlaceTimezone: null, // Guests don't have timezone detection
    level: 1,
    xp: 0,
    credits: 0,
    cosmicDust: 0,
    streak: 0,
    lastLogin: new Date().toISOString(),
    profileUpdatedAt: new Date().toISOString(),
    quests: [], // Guests don't have server-side quests
    unlockedThemes: ['theme_default'],
    unlockedFrames: ['frame_default'],
    activeTheme: 'theme_default',
    activeFrame: 'frame_default',
    achievements: {},
    role: 'user',
    stats: {
      oracleAsks: 0,
      compatibilityChecks: 0,
      tarotDraws: 0,
      questsCompleted: 0,
    },
    lastTarotDraw: null,
    interpretationsCache: {},
    subscriptionStatus: 'stargazer' as const,
  };
}

/**
 * Gets debug user progress from session storage
 * This is a pure function that can be easily tested
 */
function getDebugUserProgress(): RawUserProgress | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const debugUser = sessionStorage.getItem('debugUser');
  if (!debugUser && process.env.NEXT_PUBLIC_MYSTIC_TEST_MODE === '1') {
    return createGuestUserProgress({
      name: 'Test User',
      zodiacSign: 'Pisces',
      birthDate: '1992-03-08',
      birthTime: '10:30',
      birthPlace: 'Lisbon',
    });
  }
  if (!debugUser) {
    return null;
  }
  try {
    return JSON.parse(debugUser);
  } catch (error) {
    console.error('Failed to parse debug user data:', error);
    sessionStorage.removeItem('debugUser');
    return null;
  }
}

/**
 * Determines user progress based on various state conditions
 * This function is extracted to reduce complexity in the main hook
 */
function determineUserProgress({
  shouldReturnNullForLoading,
  getDebugProgress,
  getLoggedInProgress,
  getGuestProgress,
  hasSession,
  progressData,
}: {
  shouldReturnNullForLoading: boolean;
  getDebugProgress: RawUserProgress | null;
  getLoggedInProgress: RawUserProgress | null;
  getGuestProgress: RawUserProgress | null;
  hasSession: boolean;
  progressData: RawUserProgress | null | undefined;
}): RawUserProgress | null {
  // If we're still loading, return null
  if (shouldReturnNullForLoading) {
    return null;
  }

  // Debug mode - only in development
  if (getDebugProgress) {
    return getDebugProgress;
  }

  // If logged in and profile is loaded, use backend data
  if (getLoggedInProgress) {
    return getLoggedInProgress;
  }

  // If logged in but no profile data (error case), return null
  if (hasSession && !progressData) {
    if (process.env.NODE_ENV === 'development') {
      console.log('❌ [useUserProgress] Logged in but no profile data');
    }
    return null;
  }

  // If not logged in but has onboarding data, show guest version
  if (getGuestProgress) {
    return getGuestProgress;
  }

  // If nothing, return null
  if (process.env.NODE_ENV === 'development') {
    console.log('🚫 [useUserProgress] No user data available');
  }
  return null;
}

/**
 * Simplified user progress hook that centralizes user state management
 *
 * This hook handles:
 * - Logged-in user state (fetches from backend)
 * - Guest user state (creates local progress)
 * - Safe cleanup of guest data after successful login
 * - Debug mode support
 *
 * The backend now handles profile creation and migration, so this hook
 * focuses on simple state management and transitions.
 */
export function useUserProgressSource() {
  const { data: session, isPending: isSessionLoading } = authClient.useSession();
  const hasSession = Boolean(session?.user);

  // Guest state comes from localStorage (zustand persist), which the server
  // cannot see. Gating it behind mount keeps the first client render identical
  // to the SSR output — otherwise guests with persisted onboarding data
  // produce a UserContext mismatch during hydration (Sentry issue 7654694161).
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const {
    data: progressData,
    isLoading: isProgressLoading,
    error: progressError,
  } = trpc.user.progress.getProgress.useQuery(undefined, {
    enabled: hasSession, // Only fetch progress data if user is logged in
    staleTime: 60_000,
    retry: 1,
  });

  const { onboardingData: guestData, clearGuestDataSafely, migrationAttempts } = useGuestStore();

  // Keep guest data available when a migration attempt failed so it can be retried.
  const canClearGuestData = migrationAttempts === 0;

  // Define loading states more explicitly
  const isInitialLoading = isSessionLoading;
  const isProfileLoading = hasSession && isProgressLoading && !progressError;
  const isLoading = isInitialLoading || isProfileLoading;
  const shouldReturnNullForLoading = useMemo(() => {
    return isInitialLoading || (hasSession && isProfileLoading);
  }, [isInitialLoading, hasSession, isProfileLoading]);

  // Effect to clean up guest data after a successful login and profile load.
  useEffect(() => {
    if (hasSession && progressData && guestData && !isProfileLoading && canClearGuestData) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 [useUserProgress] Logged in with profile data. Clearing guest data.');
      }
      clearGuestDataSafely(true);
    }
  }, [
    hasSession,
    progressData,
    guestData,
    isProfileLoading,
    canClearGuestData,
    clearGuestDataSafely,
  ]);

  // Helper function to get debug user progress
  const getDebugProgress = useMemo<RawUserProgress | null>(() => {
    if (
      process.env.NODE_ENV === 'development' ||
      process.env.NEXT_PUBLIC_MYSTIC_TEST_MODE === '1'
    ) {
      return getDebugUserProgress();
    }
    return null;
  }, []);

  // Helper function to get logged in user progress
  const getLoggedInProgress = useMemo<RawUserProgress | null>(() => {
    if (hasSession && progressData && Object.keys(progressData).length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔑 [useUserProgress] Using backend progress data');
      }
      return progressData;
    }
    return null;
  }, [hasSession, progressData]);

  // Helper function to get guest user progress
  const getGuestProgress = useMemo<RawUserProgress | null>(() => {
    if (!hasSession && guestData && isMounted) {
      if (process.env.NODE_ENV === 'development') {
        console.log('👤 [useUserProgress] Using guest data');
      }
      return createGuestUserProgress(guestData);
    }
    return null;
  }, [hasSession, guestData, isMounted]);

  // Determine user progress with clearer state management
  const userProgress = useMemo<RawUserProgress | null>(() => {
    return determineUserProgress({
      shouldReturnNullForLoading,
      getDebugProgress,
      getLoggedInProgress,
      getGuestProgress,
      hasSession,
      progressData,
    });
  }, [
    shouldReturnNullForLoading,
    getDebugProgress,
    getLoggedInProgress,
    getGuestProgress,
    hasSession,
    progressData,
  ]);

  const normalizedUserProgress = useMemo(() => {
    return normalizeUserProgress(userProgress);
  }, [userProgress]);

  return {
    userProgress: normalizedUserProgress,
    isLoading,
    isLoggedIn: hasSession || Boolean(getDebugProgress),
    progressError,
    profileMissing: Boolean(progressError?.data?.code === 'NOT_FOUND'),
    shouldPromptOnboarding:
      (!hasSession && Boolean(guestData)) || Boolean(progressError?.data?.code === 'NOT_FOUND'),
  };
}
